// Utility functions for generating structured data (Schema.org JSON-LD)

export interface PropertySchemaData {
    id: string;
    title: string;
    description: string;
    location: string;
    latitude?: number;
    longitude?: number;
    price: number;
    bedrooms: number;
    bathrooms: number;
    type: string;
    images: string[];
    amenities: string[];
    agentId: string;
    slug?: string;
}

export const generateRealEstateListingSchema = (property: PropertySchemaData, agentName?: string) => {
    const baseUrl = 'https://rooms.indigobeans.co.za';
    const propertyUrl = `${baseUrl}/room/${property.slug || property.id}`;

    return {
        '@context': 'https://schema.org',
        '@type': 'RealEstateListing',
        name: property.title,
        description: property.description,
        url: propertyUrl,
        image: property.images,
        rentalPrice: {
            '@type': 'MonetaryAmount',
            value: property.price,
            currency: 'ZAR',
            unitText: 'MONTH'
        },
        address: {
            '@type': 'PostalAddress',
            addressLocality: property.location,
            addressCountry: 'ZA'
        },
        ...(property.latitude && property.longitude && {
            geo: {
                '@type': 'GeoCoordinates',
                latitude: property.latitude,
                longitude: property.longitude
            }
        }),
        numberOfRooms: property.bedrooms,
        numberOfBathroomsTotal: property.bathrooms,
        accommodationType: property.type,
        amenityFeature: property.amenities.map(amenity => ({
            '@type': 'LocationFeatureSpecification',
            name: amenity
        })),
        ...(agentName && {
            provider: {
                '@type': 'Organization',
                name: agentName
            }
        })
    };
};

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url
        }))
    };
};

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    };
};

export const generateOrganizationSchema = () => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'IndigoBeans Rooms',
        url: 'https://rooms.indigobeans.co.za',
        logo: 'https://rooms.indigobeans.co.za/logo.png',
        description: 'Find affordable, verified rooms for rent across South Africa. Browse student accommodation and rooms by location, price, and amenities.',
        address: {
            '@type': 'PostalAddress',
            addressCountry: 'ZA'
        },
        sameAs: [
            // Add social media URLs when available
        ]
    };
};

export const generatePlaceSchema = (
    name: string,
    description: string,
    latitude?: number,
    longitude?: number,
    address?: string
) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Place',
        name,
        description,
        ...(address && {
            address: {
                '@type': 'PostalAddress',
                addressLocality: name,
                addressRegion: address,
                addressCountry: 'ZA'
            }
        }),
        ...(latitude && longitude && {
            geo: {
                '@type': 'GeoCoordinates',
                latitude,
                longitude
            }
        })
    };
};

export const generateOfferSchema = (
    itemName: string,
    price: number,
    availability: 'InStock' | 'OutOfStock' | 'PreOrder' = 'InStock'
) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Offer',
        itemOffered: {
            '@type': 'Product',
            name: itemName
        },
        price,
        priceCurrency: 'ZAR',
        availability: `https://schema.org/${availability}`
    };
};
